import Network from 'shared/libraries/network';
import CONFIG from 'shared/constants/config';
import {
  TRequesPollstList,
  TUnionPollResult,
} from 'pages/pollsPage/model/types/pollsSchema';
import { isRequestCanceled } from 'shared/utils/isRequestCanceled';

const generateUUID = (): string => {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }

  const getRandomHex = (length: number): string => {
    if (
      typeof crypto !== 'undefined' &&
      typeof crypto.getRandomValues === 'function'
    ) {
      const bytes = new Uint8Array(length);
      crypto.getRandomValues(bytes);
      return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join(
        '',
      );
    }

    let result = '';
    for (let i = 0; i < length; i++) {
      result += Math.floor(Math.random() * 256)
        .toString(16)
        .padStart(2, '0');
    }
    return result;
  };

  const hex = getRandomHex(16);
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-4${hex.slice(13, 16)}-${(
    parseInt(hex.slice(16, 18), 16) & 0x3f
  )
    .toString(16)
    .padStart(2, '0')}${hex.slice(18, 20)}-${hex.slice(20, 32)}`;
};

const getAbortReason = (signal?: AbortSignal): Error => {
  if (signal?.reason instanceof Error) {
    return signal.reason;
  }

  return new DOMException('The operation was aborted.', 'AbortError');
};

const SMALL_VIDEO_PROCESSING_MS_PER_MB = 9_000;
const CHUNK_PROCESSING_MS_PER_MB = 2_500;

const createProcessingProgress = (
  fileSize: number,
  msPerMb: number,
  onProgress: (progress: number) => void,
): (() => void) => {
  const intervalMs = (fileSize / (1024 * 1024)) * (msPerMb / 100);
  let progress = 1;
  onProgress(progress);

  let timer: ReturnType<typeof setInterval> | null = setInterval(() => {
    progress += 1;

    if (progress < 100) {
      onProgress(progress);
      return;
    }

    if (timer) {
      clearInterval(timer);
      timer = null;
    }
  }, intervalMs);

  return () => {
    if (timer) {
      clearInterval(timer);
      timer = null;
    }
  };
};

const delay = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(getAbortReason(signal));
      return;
    }

    const pending: { timeoutId?: ReturnType<typeof setTimeout> } = {};

    const handleAbort = () => {
      if (pending.timeoutId !== undefined) {
        clearTimeout(pending.timeoutId);
      }
      reject(getAbortReason(signal));
    };

    pending.timeoutId = setTimeout(() => {
      signal?.removeEventListener('abort', handleAbort);
      resolve();
    }, ms);

    signal?.addEventListener('abort', handleAbort, { once: true });
  });

class PollModel extends Network {
  constructor(baseURL: string, timeout = 120000) {
    super(baseURL, timeout);
  }

  //GET

  async getPollsList<T>(data: TRequesPollstList): Promise<T> {
    return this.sendGetNoStore(`/polls/respondent/`, data);
  }

  async getPollDetails<T>(poll: string): Promise<T> {
    return this.sendGetNoStore(`/polls/respondent/${poll}`);
  }

  async getTargetQuestion<T>(): Promise<T> {
    return this.send('get', `/respondent/target/questions/ask`);
  }

  //POST
  async postPollResults<T>(result: TUnionPollResult): Promise<T> {
    return this.send('post', `/polls/respondent/result`, { ...result });
  }

  async postPollBook<T>(poll: string): Promise<T> {
    return this.send('post', `/polls/respondent/${poll}/book`);
  }

  async postPollStart<T>(poll: string): Promise<T> {
    return this.send('post', `/polls/respondent/${poll}/start`);
  }

  async postPollRefuse<T>(data: { poll: string; reason: string }): Promise<T> {
    return this.send('post', `/polls/respondent/${data.poll}/refuse`, {
      refusal_reason: data.reason,
    });
  }

  async postTaskMedia<T>(data: {
    pollId: number;
    file: File;
    media_type: 'image' | 'video' | 'audio';
    onChunkProgress?: (progress: number) => void;
    onProcessingProgress?: (progress: number) => void;
    signal?: AbortSignal;
  }): Promise<T> {
    const {
      pollId,
      file,
      media_type,
      onChunkProgress,
      onProcessingProgress,
      signal,
    } = data;
    const chunkSize = 5 * 1024 * 1024;

    if (
      (media_type === 'video' || media_type === 'audio') &&
      file.size > chunkSize
    ) {
      return this.uploadChunks({
        pollId,
        file,
        media_type,
        onChunkProgress,
        onProcessingProgress,
        signal,
      });
    }

    // Тело — байты файла: multipart на сервере закрыт
    const headers = { 'Content-Type': file.type || 'application/octet-stream' };

    if (media_type === 'video' && onProcessingProgress) {
      onChunkProgress?.(100);
      const stopProcessing = createProcessingProgress(
        file.size,
        SMALL_VIDEO_PROCESSING_MS_PER_MB,
        onProcessingProgress,
      );

      try {
        const response = await this.send<T>(
          'post',
          `/polls/respondent/${pollId}/media`,
          file,
          { headers, signal },
        );
        onProcessingProgress(100);
        return response;
      } finally {
        stopProcessing();
      }
    }

    return this.send('post', `/polls/respondent/${pollId}/media`, file, {
      headers,
      signal,
      onUploadProgress: (event: { loaded: number; total?: number }) => {
        if (!onChunkProgress || !event.total) {
          return;
        }

        onChunkProgress(
          Math.min(100, Math.round((event.loaded * 100) / event.total)),
        );
      },
    });
  }

  private async uploadChunks<T>(data: {
    pollId: number;
    file: File;
    media_type: 'video' | 'audio';
    onChunkProgress?: (progress: number) => void;
    onProcessingProgress?: (progress: number) => void;
    signal?: AbortSignal;
  }): Promise<T> {
    const {
      pollId,
      file,
      media_type,
      onChunkProgress,
      onProcessingProgress,
      signal,
    } = data;
    const chunkSize = 5 * 1024 * 1024;
    const totalChunks = Math.ceil(file.size / chunkSize);
    const uploadId = generateUUID();
    const fileExt = file.name.split('.').pop()?.toLowerCase() || 'bin';

    for (let index = 0; index < totalChunks; index++) {
      if (signal?.aborted) {
        throw getAbortReason(signal);
      }

      const chunk = file.slice(index * chunkSize, (index + 1) * chunkSize);
      const formData = new FormData();
      formData.append('chunk', chunk, `${uploadId}.${fileExt}`);
      formData.append('chunk_index', index.toString());
      formData.append('chunks_count', totalChunks.toString());
      formData.append('media_type', media_type);

      const maxRetries = 3;
      let lastError: Error | undefined;
      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        if (signal?.aborted) {
          throw getAbortReason(signal);
        }

        try {
          await this.send(
            'post',
            `/polls/respondent/${pollId}/media/chunk`,
            formData,
            {
              headers: { 'Content-Type': 'multipart/form-data' },
              signal,
            },
          );
          lastError = undefined;
          break;
        } catch (error) {
          if (signal?.aborted || isRequestCanceled(error)) {
            throw error instanceof Error ? error : getAbortReason(signal);
          }

          lastError = error instanceof Error ? error : new Error(String(error));
          if (attempt < maxRetries) {
            await delay(500 * (attempt + 1), signal);
          }
        }
      }

      if (lastError) {
        throw lastError;
      }

      if (onChunkProgress) {
        onChunkProgress(Math.round(((index + 1) / totalChunks) * 100));
      }
    }

    if (signal?.aborted) {
      throw getAbortReason(signal);
    }

    const stopProcessing = onProcessingProgress
      ? createProcessingProgress(
          file.size,
          CHUNK_PROCESSING_MS_PER_MB,
          onProcessingProgress,
        )
      : null;

    try {
      const formdata = new FormData();
      formdata.append('file', `${uploadId}.${fileExt}`);
      const response = await this.send<T>(
        'post',
        `/polls/respondent/${pollId}/media`,
        formdata,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          signal,
        },
      );
      onProcessingProgress?.(100);
      return response;
    } finally {
      stopProcessing?.();
    }
  }

  async postPollRun<T>(): Promise<T> {
    return this.send('post', `/polls/respondent/run`);
  }

  async postTargetAnswer<T>(data: {
    question_id: number;
    answer: string;
  }): Promise<T> {
    return this.send('post', `/respondent/target/questions/answer`, {
      ...data,
    });
  }
}

const baseUrl: string = CONFIG.REACT_APP_API_BASE_URL as string;
const pollModel = new PollModel(baseUrl);
export default pollModel;
